import { useState } from 'react';
import { useSubjectLanguageVariants } from '@features/user';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import {
  useAcceptProspectMutation,
  useBookingRequestQuery,
  useDeclineBookingRequestMutation,
} from '../store/api';

import {
  type IdentityCandidate,
  IdentityDecisionDialog,
} from './IdentityDecisionDialog';

export interface SelfBookedReviewBannerProps {
  appointmentId: string;
  bookingProspectId: string;
  /** Appointment status — 'suggested' means confirm/decline still apply. */
  status?: string | null;
  /** Called after the booking is declined (the appointment is cancelled). */
  onDeclined?: () => void;
}

/** Detail-drawer banner for a self-booked appointment without a chart.
 *
 * Same accept flow as the requests queue (GDN-2436): Confirm resolves
 * identity — auto-creating the record when nothing matches, or opening the
 * link-or-create dialog when candidates exist — and confirms the
 * appointment. An auto-confirmed booking keeps the banner until its record
 * is linked, so the "Unassigned" state is always actionable.
 */
export function SelfBookedReviewBanner({
  appointmentId,
  bookingProspectId,
  status,
  onDeclined,
}: SelfBookedReviewBannerProps) {
  const { singular } = useSubjectLanguageVariants();
  const { enqueueSnackbar } = useSnackbar();
  const { bookingRequests, error: errorTracking } = useEventTracking();

  const { data: prospectData } = useBookingRequestQuery({ bookingProspectId });
  const [acceptProspect, { isLoading: isAccepting }] =
    useAcceptProspectMutation();
  const [declineBooking, { isLoading: isDeclining }] =
    useDeclineBookingRequestMutation();
  const [candidates, setCandidates] = useState<IdentityCandidate[] | null>(
    null,
  );

  // Only two states carry a banner: pending ('suggested') and confirmed-but-
  // unpromoted ('scheduled' — the confirmed status value). A declined
  // booking's prospect is discarded, so no actions are offered after that.
  if (status !== 'suggested' && status !== 'scheduled') {
    return null;
  }

  const prospect = prospectData?.prospect;
  const requesterName =
    [prospect?.first_name, prospect?.last_name].filter(Boolean).join(' ') ||
    'this requester';
  const busy = isAccepting || isDeclining;

  const runAccept = async (body: {
    patient_id?: string | null;
    create_new?: boolean;
  }) => {
    try {
      const result = await acceptProspect({
        bookingProspectId,
        acceptProspectRequest: body,
      }).unwrap();
      if (result.decision_required) {
        setCandidates(
          (result.candidates ?? []).map(candidate => ({
            patientId: candidate.patient_id,
            name: candidate.name ?? null,
            dob: candidate.dob ?? null,
            email: candidate.email ?? null,
            phone: candidate.phone ?? null,
            confidence: candidate.confidence,
          })),
        );
        return;
      }
      setCandidates(null);
      enqueueSnackbar('Booking confirmed', { variant: 'success' });
    } catch {
      enqueueSnackbar('Could not confirm the booking', { variant: 'error' });
      errorTracking.track('Could not confirm the booking', 'booking_accept');
    }
  };

  const handleConfirm = () => {
    bookingRequests.confirmClicked({
      appointment_id: appointmentId,
      source: 'drawer',
    });
    void runAccept({});
  };

  const handleDecline = () => {
    bookingRequests.declineClicked({
      booking_prospect_id: bookingProspectId,
      source: 'drawer',
    });
    declineBooking({ bookingProspectId })
      .unwrap()
      .then(() => {
        enqueueSnackbar('Booking declined', { variant: 'success' });
        onDeclined?.();
      })
      .catch(() => {
        enqueueSnackbar('Could not decline the booking', { variant: 'error' });
        errorTracking.track('Could not decline the booking', 'booking_decline');
      });
  };

  return (
    <>
      <Alert
        severity="info"
        icon={false}
        sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {status === 'suggested'
              ? 'Self-booked appointment awaiting review'
              : `Self-booked appointment — not yet in your ${singular} records`}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              disabled={busy}
              startIcon={
                isAccepting ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <CheckCircleOutlinedIcon />
                )
              }
              onClick={handleConfirm}
            >
              Confirm
            </Button>
            {status === 'suggested' && (
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                disabled={busy}
                startIcon={
                  isDeclining ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <CloseIcon />
                  )
                }
                onClick={handleDecline}
              >
                Decline
              </Button>
            )}
          </Stack>
        </Box>
      </Alert>
      {candidates && (
        <IdentityDecisionDialog
          open
          requesterName={requesterName}
          candidates={candidates}
          subjectSingular={singular}
          isSaving={isAccepting}
          onClose={() => setCandidates(null)}
          onDecide={patientId => {
            void runAccept(
              patientId ? { patient_id: patientId } : { create_new: true },
            );
          }}
        />
      )}
    </>
  );
}
